import type {
  PayrollComputation,
  PayrollLineResult,
  PayrollPaymentStatus,
  PayrollPaymentType,
  PayrollProgress,
  ShipTime,
} from '@felix/domain';
import {
  type PayrollDeductionItem,
  type PayrollEntryKind,
  type PayrollExtraItem,
  payrollExtraTotal,
  payrollOtherDeductionTotal,
  shipTimeLocalDate,
} from '@felix/domain';
import type { PayrollPayment } from './types.js';

/**
 * Bordro ekranının saf yardımcıları (ADR 0061 §4, §7): tutar biçimi (kuruş tamsayıdan), ay adı,
 * dönem seçenekleri, ödeme süzgeci, özet kartının para birimi ve net ödeme listesi CSV'si.
 */

const localeOf = (language: string) => (language.startsWith('tr') ? 'tr-TR' : 'en-US');

/** Kuruştan dile göre iki ondalıklı tutar ("5,500.00" / "5.500,00"). */
export function formatMoney(minor: number, language: string): string {
  return new Intl.NumberFormat(localeOf(language), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(minor / 100);
}

/** Para birimiyle: "USD 5,500.00". */
export function formatMoneyWith(minor: number, currency: string, language: string): string {
  return `${currency} ${formatMoney(minor, language)}`;
}

/** Girişteki tutardan kuruş; boş ya da geçersizse `null`. */
export function toMinor(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.round(Number((n * 100).toFixed(4)));
}

export function fromMinor(minor: number | null | undefined): number | string {
  return minor === null || minor === undefined ? '' : minor / 100;
}

/** Baz puandan yüzde metni ("25.00 %"). */
export function formatPercent(bp: number | null, language: string): string {
  if (bp === null) return '—';
  return `${new Intl.NumberFormat(localeOf(language), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(bp / 100)} %`;
}

/** "Oct 2026" / "Eki 2026". */
export function monthLabel(month: string, language: string): string {
  const [year, mon] = month.split('-').map(Number);
  const date = new Date(Date.UTC(year ?? 1970, (mon ?? 1) - 1, 1));
  const name = new Intl.DateTimeFormat(language.startsWith('tr') ? 'tr-TR' : 'en-US', {
    month: 'short',
    timeZone: 'UTC',
  }).format(date);
  return `${name.replace('.', '')} ${year}`;
}

/** Dönem seçicisi: var olan dönemler ve bu ay (yoksa), yeniden eskiye. */
export function monthOptions(months: readonly string[], today: string): string[] {
  return [...new Set([today.slice(0, 7), ...months])].sort((a, b) => b.localeCompare(a));
}

export interface PaymentFilter {
  status: PayrollPaymentStatus | 'all';
  type: PayrollPaymentType | 'all';
  rank: string | 'all';
  query: string;
}

export const NO_PAYMENT_FILTER: PaymentFilter = {
  status: 'all',
  type: 'all',
  rank: 'all',
  query: '',
};

/** Ödeme defterinin süzgeci (ortağın ekranı: Payment Status, Payment Type, Rank, arama). */
export function filterPayments(
  items: readonly PayrollPayment[],
  filter: PaymentFilter,
): PayrollPayment[] {
  const query = filter.query.trim().toLocaleLowerCase();
  return items.filter(
    (p) =>
      (filter.status === 'all' || p.status === filter.status) &&
      (filter.type === 'all' || p.paymentType === filter.type) &&
      (filter.rank === 'all' || p.rank === filter.rank) &&
      (query === '' ||
        [p.name, p.reference ?? '', p.rank ?? ''].some((v) =>
          v.toLocaleLowerCase().includes(query),
        )),
  );
}

/**
 * Özet kartlarının para birimi: USD varsa o, yoksa ödenecek tutarı en büyük olan; diğerleri kartın
 * altında ayrıca (kur çevirisi yok; ADR 0061 §2).
 */
export function primaryProgress(
  rows: readonly PayrollProgress[],
): { main: PayrollProgress; others: PayrollProgress[] } | null {
  if (rows.length === 0) return null;
  const main =
    rows.find((r) => r.currency === 'USD') ??
    [...rows].sort((a, b) => b.payable - a.payable)[0] ??
    rows[0];
  if (!main) return null;
  return { main, others: rows.filter((r) => r !== main) };
}

function csvCell(value: string | number): string {
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

const plain = (minor: number) => (minor / 100).toFixed(2);

/**
 * Ortağın kalemleri ve sırası (M16b, ADR 0066): "Adamın maaşı, Home allotment, Gemide avans,
 * Slopchest, Kalan; bir de varsa ekstrası". Extra = kişinin extra kalemleri (ADR 0068; satırın eski
 * fazla mesai ve diğer kazanç alanları dahil). Hesap değişmez (ADR 0061 §2).
 */
export const PAYROLL_ITEMS = [
  'wages',
  'extra',
  'homeAllotment',
  'cashAdvance',
  'slopchest',
  'otherDeductions',
  'balance',
] as const;
export type PayrollItem = (typeof PAYROLL_ITEMS)[number];

/** Kalemin tablo başlığı (`payroll.columns.*`). */
export const ITEM_COLUMN: Readonly<Record<PayrollItem, string>> = {
  wages: 'wages',
  extra: 'extra',
  homeAllotment: 'allotment',
  cashAdvance: 'advances',
  slopchest: 'slopchest',
  otherDeductions: 'otherDeductions',
  balance: 'balance',
};

/**
 * Yalnız varsa gösterilen kalem: diğer kesinti (satırın eski alanı ve internet, telefon, şirket
 * türü gibi kesintiler; ADR 0080 §3). Extra sütunu her zaman görünür (ortağın geri
 * bildirimi 2026-10-17: "listede extra görünmüyor"; ADR 0068 §5).
 */
const OPTIONAL_ITEMS: ReadonlySet<PayrollItem> = new Set(['otherDeductions']);

type ItemLine = Pick<
  PayrollLineResult,
  | 'basic'
  | 'id'
  | 'currency'
  | 'overtimeHours'
  | 'overtimeRate'
  | 'overtimeAmount'
  | 'otherEarnings'
  | 'otherEarningsNote'
  | 'extras'
  | 'allotmentTotal'
  | 'advances'
  | 'slopchest'
  | 'otherDeductions'
  | 'net'
> & { deductionEntries?: PayrollLineResult['deductionEntries'] | undefined };

/** Satırın kalem tutarı (kuruş). */
export function itemAmount(line: ItemLine, item: PayrollItem): number {
  switch (item) {
    case 'wages':
      return line.basic;
    case 'extra':
      return payrollExtraTotal(line);
    case 'homeAllotment':
      return line.allotmentTotal;
    case 'cashAdvance':
      return line.advances;
    case 'slopchest':
      return line.slopchest;
    case 'otherDeductions':
      // Eski tek alan + türlü kesintiler (ADR 0080 §3); eski kapanış özetinde döküm yok
      return payrollOtherDeductionTotal(line);
    case 'balance':
      return line.net;
  }
}

/** Tabloda görünen kalemler: diğer kesinti yalnız dönemde sıfırdan farklıysa. */
export function visibleItems(lines: readonly ItemLine[]): PayrollItem[] {
  return PAYROLL_ITEMS.filter(
    (item) => !OPTIONAL_ITEMS.has(item) || lines.some((l) => itemAmount(l, item) !== 0),
  );
}

export type PayrollItemTotal = { currency: string; people: number; payable: number } & Record<
  PayrollItem,
  number
>;

/**
 * Para birimine göre kalem toplamları (kur çevirisi yok; ADR 0061 §2). Kişi sayısı, kalan ve
 * toplam ödenecek hesabın kendi toplamlarından; kalemler satırlardan.
 */
export function itemTotals(computation: PayrollComputation): PayrollItemTotal[] {
  return computation.totals.map((total) => {
    const lines = computation.lines.filter((l) => l.currency === total.currency);
    const sum = (item: PayrollItem) => lines.reduce((acc, l) => acc + itemAmount(l, item), 0);
    return {
      currency: total.currency,
      people: total.people,
      wages: sum('wages'),
      extra: sum('extra'),
      homeAllotment: sum('homeAllotment'),
      cashAdvance: sum('cashAdvance'),
      slopchest: sum('slopchest'),
      otherDeductions: sum('otherDeductions'),
      balance: total.net,
      payable: total.payable,
    };
  });
}

/**
 * Dönemin net ödeme listesi (ADR 0061 §7 "Payroll Reports"): kişi, rütbe, para birimi, ortağın
 * kalemleri sırasıyla (M16b) ve toplam ödenecek (kalan + Home Allotment); para birimine göre
 * toplam. CSV'de bütün kalem sütunları her zaman vardır (sabit biçim). Sayılar noktalı.
 */
export function netPaymentCsv(
  computation: PayrollComputation,
  label: (key: string) => string,
): string {
  const head = ['name', 'rank', 'currency', ...PAYROLL_ITEMS, 'payable'];
  const rows = computation.lines.map((l) =>
    [
      l.name,
      l.rank ?? '',
      l.currency,
      ...PAYROLL_ITEMS.map((item) => plain(itemAmount(l, item))),
      plain(l.net + l.allotmentTotal),
    ]
      .map(csvCell)
      .join(','),
  );
  const totals = itemTotals(computation).map((t) =>
    [
      label('total'),
      '',
      t.currency,
      ...PAYROLL_ITEMS.map((item) => plain(t[item])),
      plain(t.payable),
    ]
      .map(csvCell)
      .join(','),
  );
  return [head.map((h) => csvCell(label(h))).join(','), ...rows, ...totals].join('\r\n');
}

/** Extra kaleminin tür adı: hazır türün çevirisi ya da şirket türünün adı (ADR 0068 §2). */
export function extraTypeLabel(
  item: Pick<PayrollExtraItem, 'type' | 'typeName'>,
  t: (key: string) => string,
): string {
  if (item.type === 'company') return item.typeName ?? t('payroll.extraTypes.company');
  return t(`payroll.extraTypes.${item.type}`);
}

/**
 * Extra kaleminin tam etiketi: tür, açıklama ve miktar × birim ücret ("Extra overtime · Cargo
 * ops (12.5 × 15.00)").
 */
export function extraLabel(
  item: PayrollExtraItem,
  t: (key: string) => string,
  language: string,
): string {
  const parts = [extraTypeLabel(item, t)];
  if (item.description) parts.push(item.description);
  const text = parts.join(' · ');
  return item.quantity !== null && item.rate !== null
    ? `${text} (${new Intl.NumberFormat(localeOf(language), { maximumFractionDigits: 1 }).format(item.quantity)} × ${formatMoney(item.rate, language)})`
    : text;
}

/**
 * Kesinti türünün adı (ADR 0080 §2): hazır türün çevirisi ya da şirket türünün adı (Türkçede
 * Türkçe adı varsa o).
 */
export function deductionTypeLabel(
  item: {
    type: PayrollEntryKind;
    typeName?: string | null | undefined;
    typeNameTr?: string | null | undefined;
  },
  t: (key: string) => string,
  language: string,
): string {
  if (item.type === 'company') {
    const tr = language.startsWith('tr') ? item.typeNameTr : null;
    return tr || item.typeName || t('payroll.kinds.company');
  }
  return t(`payroll.kinds.${item.type}`);
}

/** Kesinti kaleminin etiketi: tür ve açıklama ("Internet / Wi-Fi data · Starlink 20 GB"). */
export function deductionLabel(
  item: PayrollDeductionItem,
  t: (key: string) => string,
  language: string,
): string {
  const label = deductionTypeLabel(item, t, language);
  return item.description ? `${label} · ${item.description}` : label;
}

/** Saat dilimindeki bugün, `YYYY-MM-DD` (gemide gemi saati; ofiste UTC). */
export function todayIn(timeZone: ShipTime, now: Date = new Date()): string {
  // Saat çizelgesi verilirse gemi saati kaydından (ADR 0065 §4)
  if (typeof timeZone !== 'string') return shipTimeLocalDate(timeZone, now.getTime());
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}
